import { Text, TextButton } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { MuiMarkdown } from "@src/appV2/lib/Markdown/MuiMarkdown";
import { InterviewStatus } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useMemo } from "react";
import { Link, useHistory } from "react-router-dom";

import { type PlacementDetail } from "../api/useGetPlacement";
import { getPlacementChatPath } from "../paths";
import { PlacementApplicationStatus } from "../types/fetchPlacements.schema";
import { getFormattedInterviewDate } from "../utils";

interface PlacementApplicationMessageProps {
  interviews: PlacementDetail["interviews"];
  workerTimezone: string;
  hasPendingInterviewRequests: boolean;
  applicationStatus?: PlacementApplicationStatus;
  workplaceName: string;
  placementId: string;
  workplaceId: string;
  interviewInstructions?: string;
}

export function PlacementApplicationMessage(props: PlacementApplicationMessageProps) {
  const {
    interviews,
    workerTimezone,
    hasPendingInterviewRequests,
    applicationStatus,
    workplaceName,
    placementId,
    workplaceId,
    interviewInstructions,
  } = props;

  const history = useHistory();

  const { hasBookedInterview, title, subtitle } = useMemo(() => {
    const bookedInterviews = interviews.filter(
      (interview) => interview.attributes.status === InterviewStatus.BOOKED
    );

    const cancelledInterview = interviews
      .sort((a, b) => {
        return new Date(b.attributes.start).getTime() - new Date(a.attributes.start).getTime();
      })
      .find((interview) => interview.attributes.status === InterviewStatus.CANCELLED);

    if (isDefined(cancelledInterview) && bookedInterviews.length === 0) {
      const isPhoneScreen = cancelledInterview.attributes.type === "phone-screen";
      return {
        hasBookedInterview: false,
        title: isPhoneScreen
          ? "Your Pre-Interview Phone Call was cancelled!"
          : "Your Interview was cancelled!",
        subtitle: `Your ${
          isPhoneScreen ? "phone call" : "interview"
        } was for ${getFormattedInterviewDate(cancelledInterview, workerTimezone)} was cancelled.`,
      };
    }

    if (bookedInterviews.length > 0) {
      const phoneScreenInterview = bookedInterviews.find(
        (interview) => interview.attributes.type === "phone-screen"
      );
      const inPersonInterview = bookedInterviews.find(
        (interview) => interview.attributes.type === "in-person-interview"
      );
      const activeInterview = inPersonInterview ?? phoneScreenInterview ?? bookedInterviews[0];

      return {
        hasBookedInterview: true,
        title:
          activeInterview?.attributes.type === "phone-screen"
            ? "Your Pre-Interview Phone Call is Booked!"
            : "Your Interview is Booked!",
        subtitle: `Scheduled for ${getFormattedInterviewDate(activeInterview, workerTimezone)}`,
      };
    }

    const attendedInterview = interviews.find(
      (interview) => interview.attributes.status === InterviewStatus.ATTENDANCE_CONFIRMED
    );

    if (isDefined(attendedInterview)) {
      return {
        hasBookedInterview: false,
        title: "Your Interview was held!",
        subtitle: `Held on ${getFormattedInterviewDate(attendedInterview, workerTimezone)}`,
      };
    }

    return {
      hasBookedInterview: false,
      title: "Application submitted!",
      subtitle:
        "If the facility is interested in your qualifications, you'll receive an interview invite over email.",
    };
  }, [interviews, workerTimezone]);

  if (hasPendingInterviewRequests && applicationStatus === PlacementApplicationStatus.APPLIED) {
    return (
      <Text variant="body2">
        {workplaceName} will get back to you in 3 business days, through your Clipboard Chat. If you
        don&apos;t hear back, you can reach out to them for an update{" "}
        <TextButton
          LinkComponent={Link}
          onClick={() => {
            history.push(
              getPlacementChatPath(
                placementId,
                workplaceId,
                "chat-with-placement-candidate-via-job-request"
              )
            );
          }}
        >
          here
        </TextButton>
        .
      </Text>
    );
  }

  return (
    <Stack spacing={4}>
      <Text semibold variant="body1">
        {title}
      </Text>

      <Text variant="body2">{subtitle}</Text>

      {hasBookedInterview &&
        isDefined(interviewInstructions) &&
        interviewInstructions.trim().length > 0 && (
          <Stack spacing={3} sx={{ pt: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Text semibold variant="body1">
                Important interview details:
              </Text>
            </Stack>
            <Box sx={{ pt: 1 }}>
              <MuiMarkdown variant="body2" lineHeight="1" listMarginTop={0}>
                {interviewInstructions}
              </MuiMarkdown>
            </Box>
          </Stack>
        )}
    </Stack>
  );
}
